import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { interval, merge, Observable, Observer } from 'rxjs';
import { map, filter, take, switchMap } from 'rxjs/operators';
import { UnleashFlags } from 'src/app/core/models/constants';

import { ILatLng } from 'src/app/core/models/google-map-models';
import { ITrackingData, IParkingAddress, ITcData, ITcfApi } from 'src/app/core/models/models';
import { EnvironmentService } from 'src/app/environment.service';
import { AARouterURL } from 'src/app/modules/app-routing/enums/routing.enum';
import { GoogleMapStoreService } from 'src/app/modules/google-map/services/google-map-store/google-map-store.service';
import { AALocationModel } from 'src/app/modules/route-planner/models/route-model';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { TrackingAndSeoService } from 'src/app/shared/services/tracking-and-seo.service';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  geocoder = new google.maps.Geocoder();
  autoCompleteService = new google.maps.places.AutocompleteService();

  routerType: AARouterURL = AARouterURL.ROUTE_PLANNER;
  geoDataStore: Array<any> = window['__geo'] || [];
  reTriggerConsentBanner = false;

  constructor(
    public googleMapStore: GoogleMapStoreService,
    public envService: EnvironmentService,
    public trackingAndSeoService: TrackingAndSeoService,
    private readonly cookieService: CookieService,
    private readonly featureFlagService: FeatureFlagService
  ) {
    this.trackingAndSeoService.setAnalyticsConfig(this.envService.routeModel.utagUrl);
  }

  public updateTrackingAndSeo(pageTitle, trackingData: ITrackingData) {
    this.trackingAndSeoService.updatePageTitle(pageTitle);

    this.trackingAndSeoService.view({ ...trackingData });
  }

  /**
   * Geocoding service.
   *
   * Wraps the Google Maps API geocoding service into an observable.
   *
   * @param address The address to be searched
   * @return An observable of GeocoderResult
   */
  getGeoLocationByLatLng(latlng: ILatLng): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      // Invokes geocode method of Google Maps API geocoding.
      this.geocoder.geocode({ location: latlng, region: 'GB' }, (results: any, status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const locationName = results[0].formatted_address;

          observer.next({
            address: this.getAddress(locationName),
            locationName: locationName,
            latlng: { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() },
            placeId: results[0].place_id
          });
        } else {
          observer.error({
            error: status
          });
        }

        observer.complete();
      });
    });
  }

  private getAddress(data): IParkingAddress {
    const addressArray = data.split(',');

    const address: IParkingAddress = {
      addressLineOne: addressArray[0],
      street: addressArray[1],
      city: addressArray[2],
      postcode: addressArray[addressArray.length - 2],
      country: addressArray[addressArray.length - 1]
    };

    return address;
  }

  /**
   * Geocoding service.
   *
   * Wraps the Google Maps API geocoding service into an observable.
   *
   * @param address The address to be searched
   * @return An observable of GeocoderResult
   */
  getGeoLocationByAddress(locationModel: AALocationModel): Observable<any> {
    const location = this.geoDataStore.find((item) => item.description.toLowerCase() === locationModel.Name.toLowerCase());

    if (location) {
      locationModel.clear();

      locationModel.Name = location.description;
      locationModel.PlaceId = location.place_id;
    }

    if (locationModel.PlaceId) {
      return this.getGeoLocationByPlaceId(locationModel);
    }

    return new Observable((observer: Observer<any>) => {
      if (!locationModel.Name) {
        observer.next({});
        observer.complete();
        return;
      }
      // Invokes geocode method of Google Maps API geocoding.
      this.geocoder.geocode({ address: locationModel.Name, region: 'GB' }, (results: any, status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const latLng = results[0].geometry.location;
          const newName = results[0].formatted_address || locationModel.Name;

          const locationModel1 = new AALocationModel(
            newName,
            latLng.lat(),
            latLng.lng(),
            locationModel.RouteType,
            results[0].formatted_address,
            results[0].partial_match === undefined ? false : true
          );
          locationModel1.PlaceId = results[0].place_id;

          observer.next(locationModel1);
        } else {
          observer.error({ status: status, address: locationModel.Name });
        }
        observer.complete();
      });
    });
  }

  getPredictionAutoCompleteList(inpStr: string, callback: Function) {
    const bounds = new google.maps.LatLngBounds(new google.maps.LatLng(49.5, -10.6), new google.maps.LatLng(61, 2));
    this.autoCompleteService.getPlacePredictions(
      {
        input: inpStr,
        bounds: bounds
      },
      callback
    );
  }

  getGeoLocationByPlaceId(locationModel: AALocationModel): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.geocoder.geocode({ placeId: locationModel.PlaceId, region: 'GB' }, (results: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          locationModel.Latitude = results[0].geometry.location.lat();
          locationModel.Longitude = results[0].geometry.location.lng();

          observer.next(locationModel);
        } else {
          observer.error({ status: status, locationModel: locationModel });
        }

        observer.complete();
      });
    });
  }

  public setOneTrustScript() {
    try {
      let firstScriptEl = window.document.getElementsByTagName('script')[0];
      const scriptEl = window.document.createElement('script');
      scriptEl.src = this.envService.routeModel.oneTrustUrl;
      scriptEl.type = 'text/javascript';
      scriptEl.async = true;
      scriptEl.charset = 'utf-8'; //NOSONAR
      scriptEl.setAttribute('data-domain-script', this.envService.routeModel.oneTrustDataDomain);

      firstScriptEl.parentNode.append(scriptEl, firstScriptEl);

      firstScriptEl = window.document.getElementsByTagName('script')[1];
      const script = window.document.createElement('script');
      script.type = 'text/javascript';
      script.textContent = 'function OptanonWrapper() { }';
      firstScriptEl.parentNode.append(script, firstScriptEl);

      globalThis.window['OptanonWrapper'] = () => {
        console.log('OneTrust fully initialized');
        this.reCheckConsentBanner();
      };
    } catch (err) {
      /* eslint-disable no-console */
      console.log('Loading one trust failed');
      /* eslint-enable no-console */
    }
  }

  public setGoogleAdTagScript() {
    let firstScriptEl = window.document.getElementsByTagName('script')[3];
    const script = window.document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.envService.routeModel.googleAdUrl;
    script.crossOrigin = 'anonymous';

    firstScriptEl.parentNode.append(script, firstScriptEl);

    window['googletag'] = window['googletag'] ?? { cmd: [] };
  }

  enableGoogleAdService() {
    const hour = new Date().getHours();
    let timeOfDay;
    if (hour >= 6 && hour < 12) {
      timeOfDay = 'morning';
    } else if (hour >= 12 && hour < 18) {
      timeOfDay = 'afternoon';
    } else if (hour >= 18 && hour < 22) {
      timeOfDay = 'evening';
    } else {
      timeOfDay = 'night';
    }
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayOfWeek = dayNames[new Date().getDay()];
    googletag.cmd.push(() => {
      googletag
        .pubads()
        .setTargeting('userType', 'user is not authenticated')
        .setTargeting('page', 'routeplanner')
        .setTargeting('timeOfDay', timeOfDay)
        .setTargeting('dayOfWeek', dayOfWeek);
      googletag.setConfig({ singleRequest: true });
      googletag.enableServices();
    });
  }

  waitForConsentOnce$(): Observable<ITcData> {
    const targetEvents = new Set(['tcloaded', 'useractioncomplete']);
    const api$ = interval(100).pipe(
      map(() =>
        typeof globalThis.window !== 'undefined' && typeof globalThis.window.__tcfapi === 'function'
          ? (globalThis.window.__tcfapi as ITcfApi)
          : undefined
      ),
      filter((api): api is ITcfApi => !!api),
      take(1)
    );

    const getTcData$ = (api: ITcfApi) =>
      new Observable<ITcData>((sub) => {
        api('getTCData', 2, (tcData, success) => {
          success ? sub.next(tcData) : sub.error(new Error('getTCData failed'));
          sub.complete();
        });
      });

    const removeListener = (api) => {
      if (listenerId != null) {
        api('removeEventListener', 2, () => {}, listenerId);
      }
    };

    let listenerId: number | null = null;
    const tcfEvents$ = (api: ITcfApi) =>
      new Observable<ITcData>((sub) => {
        api('addEventListener', 2, (tcData, success) => {
          if (!success) return;
          listenerId = tcData.listenerId ?? null;
          sub.next(tcData);
        });

        return removeListener(api);
      });

    return api$.pipe(
      switchMap((api) => merge(getTcData$(api), tcfEvents$(api))),
      filter((tc) => targetEvents.has(tc.eventStatus)),
      take(1)
    );
  }

  reCheckConsentBanner() {
    const flagStatus = this.featureFlagService.isFeatureEnabled(UnleashFlags.reTriggerConsentBanner);
    if (flagStatus !== this.reTriggerConsentBanner) {
      this.reTriggerConsentBanner = flagStatus;
      if (
        this.reTriggerConsentBanner &&
        !this.cookieService.get('eupubconsent-v2') &&
        globalThis.window.OneTrust.IsAlertBoxClosedAndValid()
      ) {
        const hostname = globalThis.window.location.hostname;
        const domain = hostname.includes('theaa.com') ? '.theaa.com' : hostname;
        this.cookieService.delete('OptanonAlertBoxClosed', '/', domain);
        this.cookieService.delete('OptanonAlertBoxClosed', '/route-planner', domain);
        globalThis.window.OneTrust.LoadBanner();
      }
    }
   const anchorTag = window.document.querySelector('#onetrust-policy > div > div > p > a');
   if (anchorTag) {
     anchorTag.removeAttribute('href');
   }
  }
}
